import { useEffect, useState } from 'react';
import { X, BookOpen, FileText, Save } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { adminReportsApi, type AdminReportFull } from '../lib/reportTemplatesApi';
import SaveAsTemplateModal from './SaveAsTemplateModal';

interface Props {
  reportId: number;
  open: boolean;
  onClose: () => void;
}

export default function ReportPreviewModal({ reportId, open, onClose }: Props) {
  const { darkMode } = useDarkMode();
  const [data, setData] = useState<AdminReportFull | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);

  useEffect(() => {
    if (open && reportId) {
      setLoading(true);
      setError(null);
      adminReportsApi
        .getFull(reportId)
        .then(setData)
        .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen'))
        .finally(() => setLoading(false));
    } else {
      setData(null);
    }
  }, [open, reportId]);

  if (!open) return null;

  const card = darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/50 p-4 overflow-y-auto">
        <div className={`rounded-xl border shadow-xl max-w-4xl w-full mt-10 mb-10 ${card}`}>
          <div className={`flex items-center justify-between p-5 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                {data?.berichtstitel ?? `Bericht #${reportId}`}
              </h2>
              {data && (
                <p className={`text-xs mt-1 ${muted}`}>
                  Kunde: {data.customer.companyName} · User: {data.user.companyName ?? data.user.contactPerson} ({data.user.email})
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {data && (
                <button
                  onClick={() => setSaveOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  <Save className="w-4 h-4" />
                  Als System-Vorlage speichern
                </button>
              )}
              <button onClick={onClose} className={muted} aria-label="Schließen">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-5 max-h-[70vh] overflow-y-auto">
            {loading && <p className={muted}>Lädt…</p>}
            {error && (
              <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded">{error}</div>
            )}
            {data && (
              <div className="space-y-4">
                {data.sections.map((sec) => (
                  <details key={sec.id} className={`rounded-lg border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <summary className={`px-4 py-3 cursor-pointer font-medium flex items-center gap-2 ${darkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'}`}>
                      <FileText className="w-4 h-4" />
                      {sec.title}
                      <span className={`ml-auto text-xs ${muted}`}>
                        {sec.completed ? '✓ fertig' : `${sec.progress}%`}
                      </span>
                    </summary>
                    <div className="p-4 space-y-3">
                      {sec.content && (
                        <div>
                          <p className={`text-xs uppercase tracking-wide ${muted} mb-1`}>Bullet-Listen</p>
                          <pre className={`text-xs whitespace-pre-wrap font-mono p-2 rounded ${darkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
                            {(() => {
                              try {
                                return JSON.stringify(JSON.parse(sec.content), null, 2);
                              } catch {
                                return sec.content;
                              }
                            })()}
                          </pre>
                        </div>
                      )}
                      {sec.finalText && (
                        <div>
                          <p className={`text-xs uppercase tracking-wide ${muted} mb-1`}>Finaler Text</p>
                          <div className={`text-sm whitespace-pre-wrap p-2 rounded ${darkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
                            {sec.finalText}
                          </div>
                        </div>
                      )}
                      {sec.aiDraft && !sec.finalText && (
                        <div>
                          <p className={`text-xs uppercase tracking-wide ${muted} mb-1`}>KI-Entwurf</p>
                          <div className={`text-sm whitespace-pre-wrap p-2 rounded ${darkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
                            {sec.aiDraft}
                          </div>
                        </div>
                      )}
                      {!sec.content && !sec.aiDraft && !sec.finalText && (
                        <p className={`text-xs italic ${muted}`}>Section noch leer</p>
                      )}
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {data && (
        <SaveAsTemplateModal
          reportId={reportId}
          isAdmin={true}
          open={saveOpen}
          onClose={() => setSaveOpen(false)}
          onSaved={() => {
            setSaveOpen(false);
          }}
        />
      )}
    </>
  );
}
